import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Check, ChevronDown, RotateCcw, Search, Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const FREE_CODES = new Set(["en", "en-GB", "es", "fr", "zh", "ar"]);

const REGION_ORDER = ["free", "popular", "south_asian", "east_asian", "southeast_asian", "african", "middle_east", "european", "americas", "oceania", "other"];
const REGION_LABELS = {
  free: "Free — 5 Major Languages",
  popular: "Popular",
  south_asian: "South Asian",
  east_asian: "East Asian",
  southeast_asian: "Southeast Asian",
  african: "African",
  middle_east: "Middle East & Central Asian",
  european: "European",
  americas: "Americas & Indigenous",
  oceania: "Oceania & Pacific",
  other: "Other",
};

function categorize(l) {
  if (l.tier === "free") return "free";
  const c = l.code;
  if (["hi", "bn", "ta", "te", "mr", "gu", "kn", "ml", "pa", "ur", "ne", "si", "or", "as", "sd"].includes(c)) return "south_asian";
  if (["ja", "ko", "zh-TW", "yue", "mn"].includes(c)) return "east_asian";
  if (["th", "vi", "id", "ms", "tl", "my", "km", "lo", "jv", "su", "ceb"].includes(c)) return "southeast_asian";
  if (["sw", "yo", "ha", "ig", "pcm", "am", "zu", "xh", "af", "so", "rw", "lg", "ti", "sn", "ny", "mg", "wo"].includes(c)) return "african";
  if (["fa", "he", "ps", "ku", "az", "kk", "uz", "tk", "tg", "ky"].includes(c)) return "middle_east";
  if (["pt", "pt-BR", "de", "it", "nl", "ru", "pl", "sv", "no", "da", "fi", "el", "cs", "ro", "hu", "bg", "hr", "uk", "tr", "sq", "sr", "sk", "sl", "mk", "bs", "et", "lv", "lt", "mt", "is", "ga", "cy", "gd", "eu", "ca", "gl", "la", "eo", "ka", "hy"].includes(c)) return "european";
  if (["ht", "qu", "ay", "gn", "haw"].includes(c)) return "americas";
  if (["mi", "sm", "to", "fj"].includes(c)) return "oceania";
  return "other";
}

export function LanguageSwitcher({ collapsed = false, variant = "sidebar" }) {
  const { lang, setLang } = useI18n();
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API}/settings/languages`)
      .then(r => setLanguages(r.data.languages || []))
      .catch(() => {});
  }, []);

  // Close on outside click — auto-collapse
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) {
      document.addEventListener("mousedown", handleClick);
      setTimeout(() => searchRef.current?.focus(), 100);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const currentLang = languages.find(l => l.code === lang) || { code: "en", name: "English", native: "English", flag: "\U0001f1fa\U0001f1f8", tier: "free" };

  const selectLang = async (code) => {
    setLang(code);
    setOpen(false); // Auto-collapse
    setSearch("");
    if (token) {
      try {
        await axios.put(`${API}/settings/language`, { language: code }, { headers });
      } catch { /* silent */ }
    }
    const selected = languages.find(l => l.code === code);
    if (selected) {
      toast.success(`Language: ${selected.native}`, { description: selected.name });
    }
  };

  const resetEnglish = async (e) => {
    e.stopPropagation();
    selectLang("en");
  };

  // Filter
  const filtered = languages.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q) || l.code.toLowerCase().includes(q);
  });

  // Group by region
  const grouped = {};
  filtered.forEach(l => {
    const region = categorize(l);
    if (!grouped[region]) grouped[region] = [];
    grouped[region].push(l);
  });

  const isLanding = variant === "landing";
  const freeCount = languages.filter(l => l.tier === "free").length;
  const totalCount = languages.length;

  // === LANDING PAGE VARIANT ===
  if (isLanding) {
    return (
      <div ref={ref} className="relative">
        <button
          data-testid="landing-language-btn"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/20 transition-all"
        >
          <Globe className="w-4 h-4 text-primary" strokeWidth={2} />
          <span className="text-sm font-medium">{currentLang.flag} {currentLang.native}</span>
          <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div
            data-testid="landing-language-dropdown"
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-popover border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150 z-50"
          >
            {/* Header */}
            <div className="px-4 pt-3 pb-1">
              <p className="text-xs font-semibold">{freeCount} Free Languages · {totalCount}+ Available</p>
              <p className="text-[10px] text-muted-foreground">Text chat works in any language. TTS/STT premium for 100+ extras.</p>
            </div>

            {/* Search + Reset */}
            <div className="px-3 pb-2 space-y-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchRef}
                  data-testid="landing-language-search"
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search 100+ languages..."
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-border/50 bg-muted/20 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
              </div>
              <button
                data-testid="landing-english-reset-btn"
                onClick={resetEnglish}
                className="flex items-center gap-1.5 w-full px-3 py-2 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/30 text-blue-700 dark:text-blue-300 text-xs font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
                Reset to English
              </button>
            </div>

            {/* Language List */}
            <div className="max-h-[360px] overflow-y-auto overscroll-contain px-2 pb-2">
              {REGION_ORDER.map(region => {
                const langs = grouped[region];
                if (!langs?.length) return null;
                const isFreeRegion = region === "free";
                return (
                  <div key={region} className={cn("mb-1.5", isFreeRegion && "bg-primary/5 rounded-xl px-1.5 py-1 mb-2")}>
                    <p className={cn(
                      "text-[9px] font-bold uppercase tracking-widest px-2 py-1",
                      isFreeRegion ? "text-primary" : "text-muted-foreground/40"
                    )}>
                      {REGION_LABELS[region]}
                    </p>
                    {langs.map(l => (
                      <button
                        key={l.code}
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => selectLang(l.code)}
                        className={cn(
                          "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-left transition-colors",
                          lang === l.code ? "bg-primary/10 text-primary" : "hover:bg-muted/50 text-foreground"
                        )}
                      >
                        <span className="text-base leading-none flex-shrink-0">{l.flag}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium leading-tight truncate">{l.native}</p>
                          <p className="text-[10px] text-muted-foreground/60 leading-tight">{l.name}</p>
                        </div>
                        {l.tier === "free" && (
                          <span className="text-[8px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded-full flex-shrink-0">FREE</span>
                        )}
                        {l.tier === "premium" && (
                          <span className="text-[8px] font-medium text-amber-600 dark:text-amber-400 flex-shrink-0" title="Voice Pack required for TTS/STT">
                            <Sparkles className="w-3 h-3" />
                          </span>
                        )}
                        {lang === l.code && <Check className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={2.5} />}
                      </button>
                    ))}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-center text-xs text-muted-foreground/50 py-4">No languages found</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // === SIDEBAR VARIANT (DEFAULT) ===
  return (
    <div ref={ref} className="relative">
      {/* Trigger - More Prominent */}
      <button
        data-testid="language-switcher-btn"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 w-full rounded-xl transition-all border",
          collapsed
            ? "justify-center p-2 hover:bg-primary/10 border-transparent"
            : "px-3 py-2.5 hover:bg-primary/10 text-left border-primary/15 bg-primary/5"
        )}
        title={`Language: ${currentLang.native}`}
      >
        <Globe className="w-4.5 h-4.5 text-primary flex-shrink-0" strokeWidth={1.5} />
        {!collapsed && (
          <>
            <span className="flex-1 text-xs font-medium truncate">
              {currentLang.flag} {currentLang.native}
            </span>
            <ChevronDown className={cn("w-3.5 h-3.5 text-muted-foreground transition-transform", open && "rotate-180")} />
          </>
        )}
      </button>

      {/* Always-visible English Reset Button — Outside Dropdown */}
      {!collapsed && lang !== "en" && (
        <button
          data-testid="english-reset-always-btn"
          onClick={resetEnglish}
          className="flex items-center gap-1.5 w-full mt-1.5 px-3 py-2 rounded-xl text-[11px] font-bold bg-blue-50 dark:bg-blue-950/40 border border-blue-200/50 dark:border-blue-800/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
          Reset to English
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div
          data-testid="language-dropdown"
          className={cn(
            "absolute z-50 bg-popover border border-border rounded-2xl shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150",
            collapsed
              ? "left-full ml-2 bottom-0 w-80"
              : "left-0 bottom-full mb-2 w-full min-w-[280px]"
          )}
        >
          {/* Search + Reset */}
          <div className="p-2 border-b border-border/50 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchRef}
                data-testid="language-search"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search 100+ languages..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-border/50 bg-muted/20 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            {/* Always-visible English Reset */}
            <button
              data-testid="english-reset-btn"
              onClick={resetEnglish}
              className={cn(
                "flex items-center gap-1.5 w-full px-2.5 py-2 rounded-xl text-xs font-bold transition-colors border",
                lang === "en"
                  ? "bg-blue-100 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300"
                  : "bg-blue-50 dark:bg-blue-950/30 border-blue-200/50 dark:border-blue-800/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
              )}
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
              {lang === "en" ? "English (Current)" : "Reset to English"}
            </button>
          </div>

          {/* Language List */}
          <div className="max-h-[320px] overflow-y-auto overscroll-contain p-1.5">
            {REGION_ORDER.map(region => {
              const langs = grouped[region];
              if (!langs?.length) return null;
              const isFreeRegion = region === "free";
              return (
                <div key={region} className={cn("mb-1.5", isFreeRegion && "bg-primary/5 rounded-xl px-1 py-0.5 mb-2")}>
                  <p className={cn(
                    "text-[9px] font-bold uppercase tracking-widest px-2 py-1",
                    isFreeRegion ? "text-primary" : "text-muted-foreground/40"
                  )}>
                    {REGION_LABELS[region]}
                  </p>
                  {langs.map(l => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => selectLang(l.code)}
                      className={cn(
                        "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-left transition-colors",
                        lang === l.code ? "bg-primary/10 text-primary" : "hover:bg-muted/50 text-foreground"
                      )}
                    >
                      <span className="text-base leading-none flex-shrink-0">{l.flag}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium leading-tight truncate">{l.native}</p>
                        <p className="text-[10px] text-muted-foreground/60 leading-tight">{l.name}</p>
                      </div>
                      {l.tier === "free" && (
                        <span className="text-[8px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded-full flex-shrink-0">FREE</span>
                      )}
                      {l.tier === "premium" && (
                        <span className="text-[8px] text-amber-600 dark:text-amber-400 flex-shrink-0" title="Voice Pack for TTS">
                          <Sparkles className="w-3 h-3" />
                        </span>
                      )}
                      {lang === l.code && <Check className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={2.5} />}
                    </button>
                  ))}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-center text-xs text-muted-foreground/50 py-4">No languages found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
